import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, Navigation, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// v2.5.893 — Prefer build-time key, else fall back to the /api/vending/routing/maps-config
// runtime endpoint. Lets ops rotate the key without a frontend rebuild.
const BUILD_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * VendingRouteMap — renders a Google Map with the optimized stop order for
 * a set of machines. Falls back to a "no key" hint if REACT_APP_GOOGLE_MAPS_API_KEY
 * is missing.
 *
 * Props:
 *   machineIds: string[]  — machines to route through
 *   startLat, startLng: optional starting point (else uses machine[0])
 *   height: pixel height (default 480)
 */
export default function VendingRouteMap({ machineIds = [], startLat, startLng, height = 480 }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapsKey, setMapsKey] = useState(BUILD_MAPS_KEY || '');
  const mapRef = useRef(null);

  // v2.5.893 — Runtime key fallback for hot-rotated keys.
  useEffect(() => {
    if (BUILD_MAPS_KEY) return;
    axios.get(`${API}/api/vending/routing/maps-config`)
      .then(r => { if (r.data?.google_maps_api_key) setMapsKey(r.data.google_maps_api_key); })
      .catch(() => { /* silent — falls to no-key UI */ });
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'konnect-google-maps',
    googleMapsApiKey: mapsKey || '',
  });

  const optimize = useCallback(async () => {
    if (!machineIds.length) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/vending/routing/optimize`, {
        machine_ids: machineIds,
        start_lat: startLat ?? null,
        start_lng: startLng ?? null,
      });
      setRoute(data);
      if (data.stops.length === 0 && data.no_geo.length > 0) {
        toast.warning(`${data.no_geo.length} machine(s) missing coordinates — they were skipped from the map. Add lat/lng in the machine record to include them.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Route optimization failed');
    } finally { setLoading(false); }
  }, [machineIds, startLat, startLng]);

  useEffect(() => { if (machineIds.length) optimize(); }, [optimize, machineIds]);

  const fitBounds = useCallback((map) => {
    if (!map || !route?.stops?.length || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    route.stops.forEach(s => bounds.extend({ lat: s.lat, lng: s.lng }));
    if (route.start) bounds.extend({ lat: route.start.lat, lng: route.start.lng });
    map.fitBounds(bounds, 60);
  }, [route]);

  if (!mapsKey) {
    return (
      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-amber-800" data-testid="route-map-no-key">
        <div className="flex items-center gap-2 font-semibold"><MapPin className="h-4 w-4" /> Google Maps API key missing</div>
        <p className="text-sm mt-1">Set <code className="text-xs bg-amber-100 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code className="text-xs bg-amber-100 px-1 rounded">/app/frontend/.env</code> to enable the route map. Route optimization is still available as a text list.</p>
        {route?.stops?.length > 0 && (
          <div className="mt-3 space-y-1">
            {route.stops.map((s, i) => (
              <div key={s.machine_id} className="text-sm">{i + 1}. {s.chassis_serial || s.name} · {s.location_name || '—'} · {s.leg_km} km</div>
            ))}
            <div className="text-xs mt-2">Total: {route.total_km} km</div>
          </div>
        )}
      </div>
    );
  }

  if (loadError) return <div className="p-6 bg-rose-50 border border-rose-200 rounded-lg text-rose-700">Failed to load Google Maps: {String(loadError)}</div>;
  if (!isLoaded) return <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading Google Maps…</div>;

  const stops = route?.stops || [];
  const center = stops.length ? { lat: stops[0].lat, lng: stops[0].lng } : { lat: 14.5995, lng: 120.9842 };
  const path = stops.map(s => ({ lat: s.lat, lng: s.lng }));
  if (route?.start) path.unshift({ lat: route.start.lat, lng: route.start.lng });

  return (
    <div data-testid="route-map">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm">
          {loading ? <span className="text-gray-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> optimizing…</span>
            : route ? <span><b>{stops.length}</b> stops · <b>{route.total_km} km</b> total{route.no_geo?.length ? ` · ${route.no_geo.length} skipped (no geo)` : ''}</span>
            : <span className="text-gray-500">Waiting for machines…</span>}
        </div>
        <Button size="sm" variant="outline" onClick={optimize} disabled={loading} data-testid="route-map-recompute">
          <Navigation className="h-3.5 w-3.5 mr-1" /> Recompute
        </Button>
      </div>
      <div style={{ height, width: '100%' }} className="rounded-lg overflow-hidden border border-gray-200">
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={center}
          zoom={12}
          onLoad={(m) => { mapRef.current = m; fitBounds(m); }}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
        >
          {route?.start && (
            <Marker
              position={{ lat: route.start.lat, lng: route.start.lng }}
              label={{ text: 'S', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: '#0ea5e9',
                fillOpacity: 1,
                strokeColor: '#0369a1',
                strokeWeight: 2,
              }}
            />
          )}
          {stops.map((s, i) => (
            <Marker
              key={s.machine_id}
              position={{ lat: s.lat, lng: s.lng }}
              label={{ text: String(i + 1), color: 'white', fontSize: '12px', fontWeight: 'bold' }}
              onClick={() => setActiveMarker(s.machine_id)}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 14,
                fillColor: '#10b981',
                fillOpacity: 1,
                strokeColor: '#065f46',
                strokeWeight: 2,
              }}
            >
              {activeMarker === s.machine_id && (
                <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 700 }}>{i + 1}. {s.chassis_serial}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{s.location_name || '—'}</div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{s.leg_km} km from previous</div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
          {path.length > 1 && (
            <Polyline
              path={path}
              options={{ strokeColor: '#059669', strokeOpacity: 0.85, strokeWeight: 4, geodesic: true }}
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
}
